import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Field, FieldLabel } from "@/components/ui/field";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import {
  endOfDay,
  endOfMonth,
  endOfToday,
  endOfWeek,
  format,
  startOfDay,
  startOfMonth,
  startOfToday,
  startOfWeek,
  subDays,
  subWeeks,
} from "date-fns";
import { CalendarIcon, X } from "lucide-react";
import { useMemo } from "react";
import type { DateRange } from "react-day-picker";

interface DatePickerWithRangeProps {
  date?: DateRange;
  onSelect: (range: DateRange | undefined) => void;
  label?: string;
}

type Preset = "today" | "yesterday" | "this-week" | "last-week" | "this-month" | "custom";

export const DatePickerWithRange = ({ date, onSelect, label }: DatePickerWithRangeProps) => {
  const hasDateSelected = !!(date?.from || date?.to);

  const presets = useMemo<Record<Exclude<Preset, "custom">, DateRange>>(() => {
    const today = new Date();

    return {
      today: {
        from: startOfToday(),
        to: endOfToday(),
      },
      yesterday: {
        from: startOfDay(subDays(today, 1)),
        to: endOfDay(subDays(today, 1)),
      },
      "this-week": {
        from: startOfWeek(today, { weekStartsOn: 1 }),
        to: endOfWeek(today, { weekStartsOn: 1 }),
      },
      "last-week": {
        from: startOfWeek(subWeeks(today, 1), { weekStartsOn: 1 }),
        to: endOfWeek(subWeeks(today, 1), { weekStartsOn: 1 }),
      },
      "this-month": {
        from: startOfMonth(today),
        to: endOfMonth(today),
      },
    };
  }, []);

  const selectedPreset: Preset = useMemo(() => {
    if (!date?.from || !date?.to) return "custom";

    const from = date.from.getTime();
    const to = date.to.getTime();

    for (const [key, range] of Object.entries(presets)) {
      if (range.from?.getTime() === from && range.to?.getTime() === to) {
        return key as Preset;
      }
    }

    return "custom";
  }, [date, presets]);

  return (
    <Field>
      <FieldLabel htmlFor="date-picker-range">{label ?? "Date Range"}</FieldLabel>

      <div className="flex gap-2">
        <Popover>
          <PopoverTrigger
            render={
              <Button
                id="date-picker-range"
                variant="outline"
                className="flex-1 justify-start px-2.5 font-normal"
              >
                <CalendarIcon className="mr-2 h-4 w-4" />

                {date?.from ? (
                  date.to ? (
                    <>
                      {format(date.from, "LLL dd, y")} – {format(date.to, "LLL dd, y")}
                    </>
                  ) : (
                    format(date.from, "LLL dd, y")
                  )
                ) : (
                  <span>Pick a date</span>
                )}
              </Button>
            }
          />

          <PopoverContent className="w-auto p-0" align="start">
            <div className="flex">
              <div className="flex w-44 flex-col border-r p-2">
                <PresetButton
                  active={selectedPreset === "today"}
                  onClick={() => onSelect(presets.today)}
                >
                  Today
                </PresetButton>

                <PresetButton
                  active={selectedPreset === "yesterday"}
                  onClick={() => onSelect(presets.yesterday)}
                >
                  Yesterday
                </PresetButton>

                <PresetButton
                  active={selectedPreset === "this-week"}
                  onClick={() => onSelect(presets["this-week"])}
                >
                  This week
                </PresetButton>

                <PresetButton
                  active={selectedPreset === "last-week"}
                  onClick={() => onSelect(presets["last-week"])}
                >
                  Last week
                </PresetButton>

                <PresetButton
                  active={selectedPreset === "this-month"}
                  onClick={() => onSelect(presets["this-month"])}
                >
                  This month
                </PresetButton>

                <PresetButton
                  onClick={() => onSelect(undefined)}
                  active={selectedPreset === "custom"}
                >
                  Custom
                </PresetButton>
              </div>

              <Calendar
                mode="range"
                defaultMonth={date?.from}
                selected={date}
                onSelect={onSelect}
                numberOfMonths={2}
                captionLayout="dropdown"
                required={false}
              />
            </div>
          </PopoverContent>
        </Popover>

        {hasDateSelected && (
          <Button size="icon" variant="outline" onClick={() => onSelect(undefined)}>
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>
    </Field>
  );
};

interface PresetButtonProps {
  active?: boolean;
  children: React.ReactNode;
  onClick?: () => void;
}

function PresetButton({ active, children, onClick }: PresetButtonProps) {
  return (
    <Button
      variant={active ? "secondary" : "ghost"}
      className={cn("justify-start")}
      onClick={onClick}
    >
      {children}
    </Button>
  );
}
